<template>
  <form @submit="onSubmit">
    <vl-form-column v-vl-spacer:bottom.small>
      <PbsSelectField
        name="fromEntity"
        :label="t('productEntityDataMapping.detail.fromEntity')"
        :placeholder="$t('productEntityDataMapping.detail.fromEntity')"
        :options="fromEntityOptions"
        :custom-label="fromEntityLabel"
      />
    </vl-form-column>
    <vl-form-column v-vl-spacer:bottom.small>
      <PbsSelectField
        name="toEntity"
        :label="t('productEntityDataMapping.detail.toEntity')"
        :placeholder="$t('productEntityDataMapping.detail.toEntity')"
        :options="toEntityOptions"
        :custom-label="toEntityLabel"
      />
    </vl-form-column>
    <vl-form-column v-if="canAddAttributeMapping" v-vl-spacer:bottom.small>
      <ProductEntityDataMappingAttributesEdit
        :definition-id="definitionId"
        :parent-definition-id="parentDefinitionId"
        :from-entity-id="fromEntityId"
        :to-entity-id="toEntityId"
      />
    </vl-form-column>

    <PbsErrorField name="custom-validation" />
    <vl-action-group v-vl-spacer:bottom.small>
      <vl-button icon="save" :mod-loading="reloading" mod-icon-before type="submit">
        {{ $t('general.save') }}
      </vl-button>
      <vl-button icon="cross" mod-icon-before mod-secondary mod-error @click="editStore.closeCreate">
        {{ $t('general.cancel') }}
      </vl-button>
    </vl-action-group>
  </form>
</template>

<script lang="ts" setup>
import { computed, onMounted, watch } from 'vue';
import { useForm } from 'vee-validate';
import { useI18n } from 'vue-i18n';
import { useProductProcessDefinitionEditStore } from '@/modules/configuration/store/product-process-definition/product-process-definition-edit.store';
import { useProductEntityDataMappingStore } from '@/modules/configuration/store/product-entity-data-mapping/product-entity-data-mapping.store';
import { useProductEntityStore } from '@/modules/configuration/store/product-entity/product-entity.store';
import { useProductEntityDataMappingValidations } from './product-entity-data-validations';
import ProductEntityDataMappingAttributesEdit from './ProductEntityDataMappingAttributesEdit.vue';
import PbsSelectField from '@/components/forms/PbsSelectField.vue';
import PbsErrorField from '@/components/forms/PbsErrorField.vue';
import { ProductEntityDetailDto } from '@/api/backoffice/clients';

const { t } = useI18n();

const store = useProductEntityDataMappingStore();
const editStore = useProductProcessDefinitionEditStore();
const productEntityStore = useProductEntityStore();

const props = defineProps({
  definitionId: {
    type: Number,
    required: true
  },
  parentDefinitionId: {
    type: Number,
    required: true
  }
});

const { validationSchema } = useProductEntityDataMappingValidations(t);

const { handleSubmit, values, setValues, setFieldValue } = useForm({
  validationSchema
});

const reloading = computed((): boolean => {
  return store.reloading;
});

const fromEntityOptions = computed(() => {
  return productEntityStore.byDefinition(props.parentDefinitionId);
});

const toEntityOptions = computed(() => {
  return productEntityStore.byDefinition(props.definitionId);
});

const fromEntityId = computed(() => {
  return values.fromEntity?.id ?? 0;
});

const toEntityId = computed(() => {
  return values.toEntity?.id ?? 0;
});

const attributeMappings = computed(() => {
  return values.attributeMappings ?? [];
});

const canAddAttributeMapping = computed(() => {
  return fromEntityId.value && toEntityId.value;
});

const fromEntityLabel = (productEntity: ProductEntityDetailDto) => {
  return `${productEntity.code} (${productEntity.label})`;
};

const toEntityLabel = (productEntity: ProductEntityDetailDto) => {
  return `${productEntity.code} (${productEntity.label})`;
};

watch(fromEntityId, (newValue, oldValue) => {
  if (newValue === oldValue) {
    return;
  }

  const fromEntity = fromEntityOptions.value.find((x) => x.id === newValue);
  if (fromEntity) {
    updateFromAttributeValues(fromEntity);
  }
});

watch(toEntityId, (newValue, oldValue) => {
  if (newValue === oldValue) {
    return;
  }

  const toEntity = toEntityOptions.value.find((x) => x.id === newValue);
  if (toEntity) {
    updateToAttributeValues(toEntity);
  }
});

onMounted(async () => {
  await Promise.all([
    productEntityStore.getByDefinition(props.parentDefinitionId),
    productEntityStore.getByDefinition(props.definitionId)
  ]);
});

const updateFromAttributeValues = (fromEntity: ProductEntityDetailDto) => {
  const fromAttributes = fromEntity.model?.attributes;
  if (!fromAttributes) {
    return;
  }

  let shouldUpdate = false;
  const updatedAttributeMappings = attributeMappings.value.map((x) => {
    const mapping = { ...x };
    const fromAttribute = fromAttributes.find((x) => x.id === mapping.from?.id);
    if (!fromAttribute) {
      mapping.from = null;
      shouldUpdate = true;
    }
    return mapping;
  });

  if (shouldUpdate) {
    setValues(
      {
        ...values,
        attributeMappings: updatedAttributeMappings
      },
      true
    );
  }
};

const updateToAttributeValues = (toEntity: ProductEntityDetailDto) => {
  const toAttributes = toEntity.model?.attributes;
  if (!toAttributes) {
    return;
  }

  let shouldUpdate = false;
  const updatedAttributeMappings = attributeMappings.value.map((x) => {
    const mapping = { ...x };
    const toAttribute = toAttributes.find((x) => x.id === mapping.to?.id);
    if (!toAttribute) {
      mapping.to = null;
      shouldUpdate = true;
    }
    return mapping;
  });

  if (shouldUpdate) {
    setValues({
      ...values,
      attributeMappings: updatedAttributeMappings
    });
  }
};

const createAttributeMappingsToUpdate = () => {
  return values.attributeMappings.map((x) => ({
    id: x.id,
    fromAttributeId: x.from.id,
    toAttributeId: x.to.id
  }));
};

const onSuccess = async (values) => {
  const attributeMappings = createAttributeMappingsToUpdate();

  const createDto = {
    productProcessDefinitionId: props.definitionId,
    fromEntityId: values.fromEntity ? values.fromEntity.id : null,
    toEntityId: values.toEntity ? values.toEntity.id : null,
    attributeMappings
  };

  await store.create(props.definitionId, createDto);
};

const onInvalidSubmit = ({ values, errors, results }) => {
  // console.log(values); // current form values
  // console.log(errors); // a map of field names and their first error message
  // console.log(results); // a detailed map of field names and their validation results
};

const onSubmit = handleSubmit(onSuccess, onInvalidSubmit);

defineExpose({ onSubmit });
</script>
./product-entity-data-validations @/api/backoffice/clients
